'use client';

import type { PeripheralBalanceType } from '@morpho-org/blue-sdk';
import type { Amount, InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { format, isDefined } from '@morpho-org/morpho-ts';
import { Avatar } from '@morpho-org/ui-v6/client';
import { Skeleton } from '@morpho-org/ui-v6/server';
import { cn, formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import NumberFlow from '@number-flow/react';
import { Button, Separator, Tooltip, TooltipContent, TooltipTrigger, TruncatedTooltip } from '@repo/ui';
import { Check, Close, Edit, Trash } from '@repo/ui/icons';
import React, { type InputHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PeripheralTokenOption } from '../types';
import PeripheralSourcesStack from '../PeripheralSourcesStack';
import { useAssetInput } from '../useAssetInput';
interface LargeAssetInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  className?: string;
  topRightElement?: React.ReactNode;
  header: string;
  token: InterfaceToken | null | undefined;
  amount: Amount;
  maxValue?: Amount;
  isMaxLoading?: boolean;
  isMax?: boolean;
  onMaxClick?: (value: boolean) => void;
  onChange: (value: bigint) => void;
  onPendingChange?: (isPending: boolean) => void;
  debouncedTime?: number;
  disabled?: boolean;
  peripheralTokenOptions?: PeripheralTokenOption[];
  onPeripheralTokenChange?: (id: PeripheralBalanceType, isActive: boolean) => void;
  isBundlerInput?: boolean;
  isEditing?: boolean;
  isConfirmDisabled?: boolean;
  localErrorMessage?: string;
  onIsEditingChange?: (isEditing: boolean) => void;
  onEditConfirm?: () => void;
  onEditCancel?: () => void;
  onReset?: () => void;
  autoFocus?: boolean;
  children?: React.ReactNode;
  variant?: 'default' | 'secondary';
}
const LargeAssetInput = ({
  className,
  header,
  token,
  debouncedTime,
  amount,
  maxValue,
  isMaxLoading,
  isMax,
  disabled,
  peripheralTokenOptions,
  isBundlerInput,
  isEditing,
  localErrorMessage,
  children,
  onChange,
  onPendingChange,
  onMaxClick,
  onIsEditingChange,
  onEditConfirm,
  onEditCancel,
  onPeripheralTokenChange,
  onReset,
  autoFocus,
  topRightElement,
  variant = 'default'
}: LargeAssetInputProps) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    handleChange,
    valueStr,
    isPending,
    flush
  } = useAssetInput({
    value: amount,
    decimals: token?.decimals ?? 18,
    debouncedTime: debouncedTime,
    onChange
  });
  useEffect(() => {
    onPendingChange?.(isPending);
    return () => onPendingChange?.(false);
  }, [isPending, onPendingChange]);
  const isInputElementDisabled = useMemo(() => disabled || isBundlerInput && !isEditing, [disabled, isEditing, isBundlerInput]);
  const isMaxActionDisplayed = useMemo(() => onMaxClick && (isDefined(maxValue) || !isMaxLoading) && (!isBundlerInput || isEditing), [isEditing, isBundlerInput, isMaxLoading, maxValue, onMaxClick]);
  const isPeripheralsDisplayed = useMemo(() => peripheralTokenOptions && peripheralTokenOptions.length > 0 && (!isBundlerInput || isBundlerInput && isEditing), [isEditing, isBundlerInput, peripheralTokenOptions]);
  const maxLabel = formatters.short.unit(token?.symbol).digits(getDigitsFromPriceAndAmount(token?.price, maxValue)).trailingZero(!maxValue).of(maxValue ?? ZERO, token?.decimals);
  const amountPriceUsd = useMemo(() => {
    return formatters.short.unit('$').digits(2).trailingZero(false).of(token?.toUsd(amount ?? ZERO), 18);
  }, [amount, token]);
  const usdNumber = useMemo(() => {
    try {
      const number = Number(format.number.digits(2).trailingZero(false).locale('en-US').of(token?.toUsd(amount ?? ZERO), 18));
      if (isNaN(number)) throw new Error('Invalid number');
      return number;
    } catch {
      return undefined;
    }
  }, [amount, token]);
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);
  useEffect(() => {
    if (isBundlerInput && !isEditing) {
      setFocused(false);
    }
  }, [isEditing, isBundlerInput]);
  const handleMaxClick = useCallback(() => !isInputElementDisabled && maxValue != null ? onMaxClick?.(!isMax) : null, [isInputElementDisabled, isMax, maxValue, onMaxClick]);
  return <div className={cn('flex flex-col gap-[9px]', className)} onMouseDown={e => {
    // Keep the input focused (and its blue border) when clicking MAX or anywhere else in the input.
    // Flush the debounced amount first, as the skipped blur used to, so clicks see the typed value.
    if (e.target !== inputRef.current && document.activeElement === inputRef.current) {
      flush();
      e.preventDefault();
    }
  }}>
      <div className={cn('gap-sm p-md shadow-md dark:shadow-none flex flex-col rounded-[20px]', variant === 'default' ? 'bg-card' : 'bg-muted', focused ? 'outline-ring outline-1' : 'outline-border outline-[0.5px]', isInputElementDisabled ? 'cursor-not-allowed' : 'cursor-text', !isInputElementDisabled && !children && 'hover:bg-card')} onClick={() => !isInputElementDisabled && inputRef.current?.focus()}>
        <div className="gap-sm flex flex-row items-center justify-between">
          <TruncatedTooltip text={header}>
            {headerRef => <span ref={headerRef} className="text-secondary-foreground max-w-[170px] truncate text-xs">
                {header}
              </span>}
          </TruncatedTooltip>
          {topRightElement ? <div className="flex flex-1 items-center justify-end">
              <div className="min-w-0 overflow-hidden">{topRightElement}</div>
            </div> : <div className="flex gap-[2px]">
              {isBundlerInput && <span className="text-info-foreground text-xs">
                  {isEditing ? 'Editing Bundler' : 'Pending'}
                </span>}
              <Avatar size="small" src={token?.logoURI} />
            </div>}
        </div>
        <div className="gap-sm flex flex-row items-center justify-between">
          <input aria-label="Asset Input" ref={inputRef} placeholder="0.00" value={valueStr} autoFocus={autoFocus} onChange={e_0 => {
          if (!isInputElementDisabled) handleChange(e_0.target.value);
          if (isMax) onMaxClick?.(false);
        }} onFocus={() => {
          setFocused(true);
        }} onBlur={() => {
          flush();
          setFocused(false);
        }} disabled={isInputElementDisabled} inputMode="decimal" lang="en-US" data-testid="asset-input" className={cn('text-xl text-foreground h-[33px] w-full border-none bg-transparent p-0 outline-none', 'caret-primary truncate', 'placeholder:text-muted-foreground disabled:cursor-not-allowed')} />
          {isBundlerInput && !isEditing && <div className="flex flex-row items-center gap-[10px]">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="iconSm" onClick={() => onIsEditingChange?.(true)} aria-label="Edit operation">
                    <Edit />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit operation.</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="iconSm" onClick={() => {
                onReset?.();
              }} aria-label="Remove operation from bundler">
                    <Trash />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove operation from bundler.</TooltipContent>
              </Tooltip>
            </div>}
          {isBundlerInput && isEditing && <div className="flex flex-row items-center gap-[10px]">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="iconSm" disabled={!!localErrorMessage} onClick={() => {
                setFocused(false);
                onIsEditingChange?.(false);
                onEditConfirm?.();
              }} aria-label="Confirm editing">
                    <Check />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {localErrorMessage ? <span className="text-3xs break-all">{localErrorMessage}</span> : 'Confirm editing.'}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="iconSm" onClick={() => {
                onIsEditingChange?.(false);
                onEditCancel?.();
              }} aria-label="Cancel editing">
                    <Close />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Cancel editing.</TooltipContent>
              </Tooltip>
            </div>}
        </div>
        <div className="gap-sm flex min-h-[26px] w-full flex-row items-center justify-between">
          <span className={cn('text-3xs h-[18px] shrink-0 leading-none whitespace-nowrap', !focused || isInputElementDisabled ? 'text-muted-foreground' : 'text-secondary-foreground')}>
            {/* Desktop: NumberFlow, Mobile: plain text */}
            {isDefined(usdNumber) ? <>
                <span className="hidden md:inline">
                  <NumberFlow value={usdNumber} prefix="$" locales="en-US" format={{
                notation: 'compact',
                compactDisplay: 'short',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                trailingZeroDisplay: 'stripIfInteger'
              }} />
                </span>
                <span className="inline md:hidden">{amountPriceUsd}</span>
              </> : amountPriceUsd}
          </span>

          {isMaxLoading && !isMaxActionDisplayed && <Skeleton className="h-[26px] w-[112px]" />}

          {isMaxActionDisplayed ? <div className="gap-xs flex min-w-0 flex-row items-center">
              {maxValue ? <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="text-3xs text-secondary-foreground min-w-0 truncate whitespace-nowrap">
                      {maxLabel}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {formatters.short.unit(token?.symbol).digits(8).trailingZero(false).of(maxValue, token?.decimals)}
                  </TooltipContent>
                </Tooltip> : <TruncatedTooltip text={maxLabel ?? ''}>
                  {maxLabelRef => <span ref={maxLabelRef} className="text-3xs text-secondary-foreground min-w-0 truncate whitespace-nowrap">
                      {maxLabel}
                    </span>}
                </TruncatedTooltip>}

              <Button variant="secondary" size="sm"
          // 26px matches the loading skeleton / row min-height and the pre-@repo/ui
          // `size="small"` height (@repo/ui `sm` is 28px).
          className="h-[26px]" data-testid="asset-input-max" disabled={isInputElementDisabled || isMax || !maxValue} onClick={handleMaxClick}>
                MAX
              </Button>
            </div> : null}
        </div>
        {children && <>
            <Separator />
            <div>{children}</div>
          </>}
      </div>
      {isPeripheralsDisplayed && peripheralTokenOptions && <PeripheralSourcesStack token={token} peripheralTokenOptions={peripheralTokenOptions} isInputElementDisabled={isInputElementDisabled} onPeripheralTokenChange={onPeripheralTokenChange} />}
    </div>;
};
export default LargeAssetInput;